/**
 * Service grouping logic for the Mission Control liveness chart.
 *
 * This module is React-free so it can be imported by both the client bundle and
 * the Node test runner under server/__tests__/service-groups.test.ts.
 */

export type ServiceGroup =
  | 'System'
  | 'Intel: Ingestion'
  | 'Intel: Predictions'
  | 'Intel: Claims'
  | 'Intel: Other'
  | 'GameGate'
  | 'Other';

/**
 * Ordered list of service groups as they appear in the liveness chart.
 * Keep this in sync with every place that renders the chart.
 */
export const SERVICE_GROUPS: readonly ServiceGroup[] = [
  'System',
  'Intel: Ingestion',
  'Intel: Predictions',
  'Intel: Claims',
  'Intel: Other',
  'GameGate',
  'Other',
];

export const GROUP_HEADER_COLORS: Record<ServiceGroup, string> = {
  System: 'bg-indigo-500',
  'Intel: Ingestion': 'bg-sky-600',
  'Intel: Predictions': 'bg-purple-500',
  'Intel: Claims': 'bg-amber-600',
  'Intel: Other': 'bg-fuchsia-500',
  GameGate: 'bg-emerald-500',
  Other: 'bg-slate-500',
};

export const GROUP_NOTES: Partial<Record<ServiceGroup, string>> = {
  'Intel: Ingestion':
    'Upstream of the funnel — produces the episodes the funnel starts from.',
  'Intel: Claims':
    'Separate pipeline — shares transcripts with predictions but is not part of the prediction funnel.',
};

const INTEL_INGESTION_IDS = new Set([
  'intel_fetch_episodes',
  'intel_fetch_transcripts',
]);

const INTEL_PREDICTIONS_IDS = new Set([
  'intel_extract_predictions',
  'intel_extract_structure',
  'intel_extract_conditions',
  'intel_score_predictions',
]);

const INTEL_CLAIMS_IDS = new Set(['intel_extract_claims']);

/**
 * Order of services inside the Intel: Predictions group. Must stay in sync with
 * FUNNEL_STAGE_ORDER in server/lib/funnel-liveness.ts.
 */
const INTEL_PREDICTIONS_ORDER = [
  'intel_extract_predictions',
  'intel_extract_structure',
  'intel_extract_conditions',
  'intel_score_predictions',
];

const PREDICTIONS_ORDER_INDEX = new Map(
  INTEL_PREDICTIONS_ORDER.map((id, idx) => [id, idx])
);

export function groupForServiceKey(key: string): ServiceGroup {
  if (INTEL_INGESTION_IDS.has(key)) return 'Intel: Ingestion';
  if (INTEL_PREDICTIONS_IDS.has(key)) return 'Intel: Predictions';
  if (INTEL_CLAIMS_IDS.has(key)) return 'Intel: Claims';
  if (/intel/i.test(key)) return 'Intel: Other';
  if (/gamegate/i.test(key)) return 'GameGate';
  if (/(linear|pipeline|webhook|agent)/i.test(key)) return 'System';
  return 'Other';
}

/**
 * Sort service entries for a given group. The Intel: Predictions group is kept
 * in funnel order; all other groups preserve their original order.
 */
export function sortGroupEntries<T>(
  group: ServiceGroup,
  entries: [string, T][]
): [string, T][] {
  if (group !== 'Intel: Predictions') return entries;
  return [...entries].sort((a, b) => {
    const ai = PREDICTIONS_ORDER_INDEX.get(a[0]) ?? Number.POSITIVE_INFINITY;
    const bi = PREDICTIONS_ORDER_INDEX.get(b[0]) ?? Number.POSITIVE_INFINITY;
    return ai - bi;
  });
}
